import { Winning } from '../types/prize.types';

export enum PRIZE_STATUS {
    AMBIGUOUS_PENDING = 'AMBIGUOUS_PENDING',
    AWAITING_RELEASE = 'AWAITING_RELEASE',
    COMPLETED = 'COMPLETED',
    HYPERWALLET_IN_PROGRESS = 'HYPERWALLET_IN_PROGRESS',
    NEEDS_REVERIFICATION = 'NEEDS_REVERIFICATION',
    NO_STATE = 'NO_STATE',
    NO_TAX_PROFILE = 'NO_TAX_PROFILE',
    PAYMENT_PENDING = 'PAYMENT_PENDING',
    OFFLINE_PAYMENT = 'OFFLINE_PAYMENT',
    VERIFICATION_PENDING = 'VERIFICATION_PENDING'
}

export type PrizeStatus = PRIZE_STATUS;

/**
 * | idVerified | intrvwUrl | rlsAccept | Status |
 * |------------|-----------|-----------|--------|
 * | FALSE      | FALSE     | FALSE     | Completed interview, verification pending
 * | FALSE      | FALSE     | TRUE      | See note 😩
 * | FALSE      | TRUE      | FALSE     | Needs to do full tax interview for verification
 * | FALSE      | TRUE      | TRUE      | Possible state, if user's tax record flips from valid to invalid (expires)
 * | TRUE       | FALSE     | TRUE      | Payment pending
 * | TRUE       | TRUE      | FALSE     | User verified, needs to do release form
 * | TRUE       | TRUE      | TRUE      | Impossible/invalid state
 *
 * 😩 This is an ambiguous state that can happen in the following situations:
 * 1. Has just completed interview and is awaiting verification
 * 2. Completed initial interview, then erased profile by entering Retake flow and not finishing
 * 3. Aborted interview after accepting release form
 *
 * There is also an additional state, `offlinePayment` or `paymentHandler = 'epic'`. If this state
 * is true then the user cannot use Hyperwallet and must manually submit payment info to Epic.
 */
export const getPrizeStatus = (
    winning: Winning,
    identityTaxVerified: boolean,
    offlinePayment: boolean
): PrizeStatus => {
    const { releaseFormAccepted, taxInterviewUrl, eventOfflinePaymentMarked } = winning;

    const idVerified = identityTaxVerified;
    const isPrizeOffline = eventOfflinePaymentMarked || offlinePayment;

    // User completed interview and verification is pending
    if (!idVerified && !taxInterviewUrl && !releaseFormAccepted) {
        return PRIZE_STATUS.VERIFICATION_PENDING;
    }

    // Needs to set up tax profile by doing a tax interview
    if (!idVerified && taxInterviewUrl && !releaseFormAccepted) {
        return PRIZE_STATUS.NO_TAX_PROFILE;
    }

    // User's tax status has changed from valid to invalid
    if (!idVerified && taxInterviewUrl && releaseFormAccepted) {
        return PRIZE_STATUS.NEEDS_REVERIFICATION;
    }

    // Payment pending (offline payment)
    if (idVerified && releaseFormAccepted && !taxInterviewUrl && isPrizeOffline) {
        return PRIZE_STATUS.OFFLINE_PAYMENT;
    }

    // Payment pending
    if (idVerified && !taxInterviewUrl && releaseFormAccepted) {
        return PRIZE_STATUS.PAYMENT_PENDING;
    }

    // User verified and can skip to release form
    if (idVerified && taxInterviewUrl && !releaseFormAccepted) {
        return PRIZE_STATUS.AWAITING_RELEASE;
    }

    // User is in ambiguous pending state
    if (!idVerified && !taxInterviewUrl && releaseFormAccepted) {
        return PRIZE_STATUS.AMBIGUOUS_PENDING;
    }

    return PRIZE_STATUS.NO_STATE;
};

export const getCurrentStep = (status: PrizeStatus): number => {
    switch (status) {
        case PRIZE_STATUS.OFFLINE_PAYMENT:
            return 3;
        case PRIZE_STATUS.PAYMENT_PENDING:
            return 3;
        case PRIZE_STATUS.AWAITING_RELEASE:
        case PRIZE_STATUS.NO_TAX_PROFILE:
            return 1;
        case PRIZE_STATUS.NEEDS_REVERIFICATION:
            return 1;
        case PRIZE_STATUS.VERIFICATION_PENDING:
        case PRIZE_STATUS.AMBIGUOUS_PENDING:
        case PRIZE_STATUS.NO_STATE:
            return 2;
        case PRIZE_STATUS.HYPERWALLET_IN_PROGRESS:
            return 3;
        case PRIZE_STATUS.COMPLETED:
            return 3;
        default:
            return 1;
    }
};
